import { useId, useState } from "react";
import { tagLabel } from "./tableState.ts";

export function ChipPicker(props: {
  inputLabel: string;
  addLabel?: string;
  selected: string[];
  choices: string[];
  allowCreate: boolean;
  disabled?: boolean;
  locked?: string[];
  removeLabel: (value: string) => string;
  matchCase?: boolean;
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const listId = useId();
  const locked = new Set((props.locked ?? []).map((value) => value.toLowerCase()));
  function same(left: string, right: string): boolean {
    return props.matchCase ? left === right : left.toLowerCase() === right.toLowerCase();
  }
  function add() {
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    const known = props.choices.find((choice) => same(choice, trimmed));
    const value = known ?? (props.allowCreate ? trimmed : "");
    if (value.length === 0) return;
    if (props.selected.some((item) => same(item, value))) {
      setDraft("");
      return;
    }
    props.onChange([...props.selected, value]);
    setDraft("");
  }
  return (
    <div className="tag-editor">
      {props.selected.map((value) => (
        <span className="tag-chip" key={value}>
          {value}
          {props.disabled || locked.has(value.toLowerCase()) ? null : (
            <button type="button" aria-label={props.removeLabel(value)} onClick={() => props.onChange(props.selected.filter((item) => item !== value))}>
              ×
            </button>
          )}
        </span>
      ))}
      {props.disabled ? null : (
        <>
          <input
            aria-label={props.inputLabel}
            list={listId}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                add();
              }
            }}
          />
          <datalist id={listId}>
            {props.choices.map((choice) => (
              <option key={choice} value={choice} />
            ))}
          </datalist>
          <button type="button" aria-label={props.addLabel ?? "Add"} onClick={add}>
            {props.addLabel ?? "Add"}
          </button>
        </>
      )}
    </div>
  );
}

export function parseTag(raw: string, choices: string[], matchCase = false): { key: string; value: string } | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const known = choices.find((choice) => (matchCase ? choice === trimmed : choice.toLowerCase() === trimmed.toLowerCase()));
  const label = known ?? trimmed;
  const split = label.indexOf(":");
  if (split <= 0) return { key: label, value: "" };
  return { key: label.slice(0, split).trim(), value: label.slice(split + 1).trim() };
}

export function TagEditor(props: {
  deviceName: string;
  tags: { key: string; value: string }[];
  choices: string[];
  disabled: boolean;
  addLabel?: string;
  onChange: (tags: { key: string; value: string }[]) => void;
}) {
  return (
    <ChipPicker
      inputLabel={`Add tag for ${props.deviceName}`}
      addLabel={props.addLabel}
      selected={props.tags.map(tagLabel)}
      choices={props.choices}
      allowCreate
      disabled={props.disabled}
      matchCase
      removeLabel={(value) => `Remove tag ${value} from ${props.deviceName}`}
      onChange={(next) => {
        const tags: { key: string; value: string }[] = [];
        const pool = props.tags.map((tag) => ({ tag, label: tagLabel(tag), used: false }));
        for (const label of next) {
          const held = pool.find((item) => !item.used && item.label === label);
          if (held) {
            held.used = true;
            tags.push(held.tag);
            continue;
          }
          const parsed = parseTag(label, props.choices, true);
          if (!parsed || parsed.key.length === 0) continue;
          if (tags.some((tag) => tag.key === parsed.key && tag.value === parsed.value)) continue;
          tags.push(parsed);
        }
        props.onChange(tags);
      }}
    />
  );
}
